import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card/Card';
import { Button } from '@/components/ui/Button/Button';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { useNotificationStore } from '@/stores/notificationStore';
import { useOrgStore } from '@/stores/orgStore';
import { useAuthStore } from '@/stores/authStore';
import { apiFetch } from '@/lib/api';
import { formatDateTimeStr } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';

interface ExportDTO {
  id: string;
  userId: string;
  orgId: string | null;
  scope: 'user' | 'org';
  format: 'json' | 'csv';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  fileSize: number | null;
  expiresAt: string | null;
  downloadUrl: string | null;
  createdAt: string;
}

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '-';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

// formatDateTimeStr imported from @/lib/localeFormatters

function StatusBadge({ status }: { status: ExportDTO['status'] }) {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {t(status)}
    </span>
  );
}

export function ExportPage() {
  usePageHeader({ title: t('Data Export'), breadcrumbs: [] });

  const [userFormat, setUserFormat] = React.useState<'json' | 'csv'>('json');
  const [orgFormat, setOrgFormat] = React.useState<'json' | 'csv'>('json');
  const [selectedOrgId, setSelectedOrgId] = React.useState<string>('');
  const queryClient = useQueryClient();
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  const orgs = useOrgStore((s) => s.orgs);
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = !!(user as any)?.isSuperAdmin;

  // For super admins, use selected org; for regular users, use active org
  const effectiveOrgId = isSuperAdmin ? selectedOrgId : activeOrgId;
  const effectiveOrg = orgs.find((o) => o.id === effectiveOrgId);

  // Fetch exports list
  const { data: exports, isLoading } = useQuery({
    queryKey: ['exports'] as const,
    queryFn: async () => {
      const res = await apiFetch('/api/exports');
      if (!res.ok) throw new Error('Failed to load exports');
      const json = await res.json();
      return (json.data || []) as ExportDTO[];
    },
    staleTime: 30_000,
    gcTime: 2 * 60_000,
  });

  // Request personal export
  const personalExportMutation = useMutation({
    mutationFn: async (format: 'json' | 'csv') => {
      const res = await apiFetch('/api/users/me/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format }),
      });
      if (!res.ok) throw new Error('Failed to request export');
      const json = await res.json();
      return json.data as ExportDTO;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exports'] });
      try { useNotificationStore.getState().success(t('Personal data export requested successfully')); } catch {}
    },
    onError: () => {
      try { useNotificationStore.getState().error(t('Failed to request data export')); } catch {}
    },
  });

  // Request org export
  const orgExportMutation = useMutation({
    mutationFn: async ({ orgId, format }: { orgId: string; format: 'json' | 'csv' }) => {
      const res = await apiFetch(`/api/orgs/${orgId}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format }),
      });
      if (!res.ok) throw new Error('Failed to request org export');
      const json = await res.json();
      return json.data as ExportDTO;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['exports'] });
      try { useNotificationStore.getState().success(t('Organization data export requested successfully')); } catch {}
    },
    onError: () => {
      try { useNotificationStore.getState().error(t('Failed to request organization data export')); } catch {}
    },
  });

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Personal Data Export */}
        <Card>
          <CardHeader>
            <CardTitle>Export My Data</CardTitle>
            <CardDescription>
              Download a copy of your personal data including your profile, organization memberships, and related information.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <select
                value={userFormat}
                onChange={(e) => setUserFormat(e.target.value as 'json' | 'csv')}
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="json">JSON</option>
                <option value="csv">CSV</option>
              </select>
              <Button
                onClick={() => personalExportMutation.mutate(userFormat)}
                disabled={personalExportMutation.isPending}
                loading={personalExportMutation.isPending}
              >
                Export Personal Data
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Organization Data Export */}
        {orgs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Export Organization Data</CardTitle>
              <CardDescription>
                Download a copy of an organization's data including members, settings, theme, translations, and AI configuration.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                {/* Org selector */}
                <div>
                  <label className="block text-sm font-medium mb-1">Organization</label>
                  <select
                    value={effectiveOrgId || ''}
                    onChange={(e) => setSelectedOrgId(e.target.value)}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm w-full max-w-xs"
                  >
                    <option value="">Select an organization...</option>
                    {orgs.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <select
                    value={orgFormat}
                    onChange={(e) => setOrgFormat(e.target.value as 'json' | 'csv')}
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="json">JSON</option>
                    <option value="csv">CSV</option>
                  </select>
                  <Button
                    onClick={() => effectiveOrgId && orgExportMutation.mutate({ orgId: effectiveOrgId, format: orgFormat })}
                    disabled={!effectiveOrgId || orgExportMutation.isPending}
                    loading={orgExportMutation.isPending}
                  >
                    Export Organization Data
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export History */}
        <Card>
          <CardHeader>
            <CardTitle>Export History</CardTitle>
            <CardDescription>
              Your recent data exports. Completed exports are available for download for 7 days.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : !exports || exports.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4">No exports yet. Request one above.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('Date')}</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('Scope')}</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('Format')}</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('Status')}</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('Size')}</th>
                      <th className="text-left py-2 px-2 font-medium text-muted-foreground">{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exports.map((exp) => (
                      <tr key={exp.id} className="border-b last:border-0">
                        <td className="py-2 px-2">{formatDateTimeStr(exp.createdAt)}</td>
                        <td className="py-2 px-2 capitalize">{exp.scope}</td>
                        <td className="py-2 px-2 uppercase">{exp.format}</td>
                        <td className="py-2 px-2"><StatusBadge status={exp.status} /></td>
                        <td className="py-2 px-2">{formatBytes(exp.fileSize)}</td>
                        <td className="py-2 px-2">
                          {exp.status === 'completed' && exp.downloadUrl ? (
                            <a
                              href={exp.downloadUrl}
                              className="text-primary hover:underline text-sm font-medium"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t('Download')}
                            </a>
                          ) : exp.status === 'failed' ? (
                            <span className="text-sm text-red-500">{t('Failed')}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground">--</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
